import type { ChapterLayout } from './layout.js'

export interface FitViewport {
  width: number
  height: number
  padding: number
  minScale?: number
}

export interface FitResult {
  scale: number
  offsetX: number
  offsetY: number
}

/**
 * Scala/trasla il layout per entrare nel viewport, senza modificarlo: il
 * layout resta identico, cambia solo la trasformazione di presentazione.
 * Allinea in alto (primo strato visibile) quando la mappa non entra.
 */
export function fitLayout(layout: ChapterLayout, viewport: FitViewport): FitResult {
  const { width, height, padding, minScale = 0 } = viewport
  const availableW = width - 2 * padding
  const availableH = height - 2 * padding
  const scaleToFitW = layout.width > 0 ? availableW / layout.width : 1
  const scaleToFitH = layout.height > 0 ? availableH / layout.height : 1
  const scale = clamp(Math.min(scaleToFitW, scaleToFitH, 1), minScale, 1)

  const scaledW = layout.width * scale
  const scaledH = layout.height * scale
  let offsetX = (width - scaledW) / 2
  if (scaledW > availableW) {
    // Non entra in larghezza: centra sul primo strato (nodi con y minima), poi
    // clampa così la mappa non lascia mai vuoto oltre il padding.
    const center = firstLayerCenterX(layout)
    if (center != null) {
      offsetX = clamp(width / 2 - center * scale, width - padding - scaledW, padding)
    }
  }
  // Allineamento in alto quando la mappa non entra in altezza; altrimenti centrata.
  const offsetY = scaledH <= availableH ? (height - scaledH) / 2 : padding

  return { scale, offsetX, offsetY }
}

function firstLayerCenterX(layout: ChapterLayout): number | null {
  const boxes = Object.values(layout.nodes)
  if (boxes.length === 0) return null
  const minY = Math.min(...boxes.map((b) => b.y))
  const first = boxes.filter((b) => b.y - minY <= 1)
  const left = Math.min(...first.map((b) => b.x - b.w / 2))
  const right = Math.max(...first.map((b) => b.x + b.w / 2))
  return (left + right) / 2
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}
